import { useEffect, useState, type ReactElement } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  EmptyState,
  PeopleIcon,
  Skeleton,
  Text,
} from '@citibox-panel/ui';
import { ReadErrorStrip } from '../../../components/ReadErrorStrip';
import { useExcludedUsers } from '../hooks/useExcludedUsers';
import { useRemoveExcludedUser } from '../hooks/useRemoveExcludedUser';
import type { ExcludedUser, ExclusionListCarrier, ExclusionListSeams } from '../lib/excludedUser';
import { ExcludedUsersTable } from './ExcludedUsersTable';
import { RemoveConfirmDialog } from './RemoveConfirmDialog';
import { neverHandledByHost, type IsHandledByHost } from '../../../lib/hostErrors';

export interface ExcludedUsersCardProps
  extends Pick<ExclusionListSeams, 'fetchExcludedUsers' | 'removeExcludedUser'> {
  carrier: ExclusionListCarrier;
  timeZone: string | null;
  isHandledByHost?: IsHandledByHost;
}

// Fixed panel copy. The server's own `message`/`detail` never reaches the DOM — an error envelope is
// attacker-influenced data like any other payload.
// TODO: i18n — copy en ES hasta Fase 1.
const READ_ERROR = 'No se ha podido cargar la lista de usuarios excluidos.';
const REMOVE_ERROR = 'No se ha podido quitar al usuario.';
const EMPTY_TITLE = 'Ningún usuario excluido';
const EMPTY_DESCRIPTION = 'Añade uno por teléfono desde la tarjeta de la izquierda';
const SKELETON_ROWS = 3;

const countLabel = (count: number): string =>
  count === 1 ? '1 usuario excluido' : `${String(count)} usuarios excluidos`;

export function ExcludedUsersCard({
  carrier,
  timeZone,
  fetchExcludedUsers,
  removeExcludedUser,
  isHandledByHost = neverHandledByHost,
}: ExcludedUsersCardProps): ReactElement {
  const { users, isPending, isError, error, retry } = useExcludedUsers(
    carrier.slug,
    fetchExcludedUsers,
  );
  const removal = useRemoveExcludedUser(carrier.slug, removeExcludedUser);
  // `null` is «no dialog»; the value is also who is being removed.
  const [pending, setPending] = useState<ExcludedUser | null>(null);

  // The dialog closes when the write SETTLES, not when it is confirmed, so focus returns to a row
  // button that is enabled again rather than to one disabled for the duration.
  useEffect(() => {
    if (!removal.isRemoving && (removal.hasRemoved || removal.isError)) {
      setPending(null);
    }
  }, [removal.isRemoving, removal.hasRemoved, removal.isError]);

  // A refusal the host already answers with its own screen gets no copy here. The other guards keep
  // the raw error, so a handled read still hides the stale count, rows and empty state.
  const showReadError = isError && !isHandledByHost(error);
  const showRemoveError = removal.isError && !isHandledByHost(removal.error);

  return (
    // Elevated at level 4, like its siblings: on the page's white panel a hairline border vanishes.
    <Card variant="elevation" elevation={4} sx={{ borderRadius: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <PeopleIcon fontSize="small" />
          <Text variant="subtitle2" component="h3" bold>
            Usuarios excluidos
          </Text>
          {/* Mounted in every state, so the region exists BEFORE its text arrives. */}
          <Box role="status" sx={{ ml: 1 }}>
            {users !== undefined && !isError && (
              <Text variant="body2" component="span">
                {`· ${countLabel(users.length)}`}
              </Text>
            )}
          </Box>
        </Box>
        {isPending &&
          !isError &&
          Array.from({ length: SKELETON_ROWS }, (_, index) => (
            <Skeleton key={index} variant="text" height={36} />
          ))}
        {showReadError && <ReadErrorStrip message={READ_ERROR} onRetry={retry} />}
        {!isError && users !== undefined && users.length === 0 && (
          <EmptyState title={EMPTY_TITLE} description={EMPTY_DESCRIPTION} />
        )}
        {!isError && users !== undefined && users.length > 0 && (
          <ExcludedUsersTable
            users={users}
            timeZone={timeZone}
            removingUserId={pending === null || !removal.isRemoving ? null : pending.userId}
            onRemove={setPending}
          />
        )}
        {/* Outside the status region: a control-less alert, but a message the reader has to act
            on, and never the server's own words. */}
        {showRemoveError && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="error">{REMOVE_ERROR}</Alert>
          </Box>
        )}
        <RemoveConfirmDialog
          user={pending}
          carrierName={carrier.name}
          isRemoving={removal.isRemoving}
          onConfirm={() => {
            if (pending !== null) removal.remove(pending.userId);
          }}
          onCancel={() => {
            setPending(null);
          }}
        />
      </CardContent>
    </Card>
  );
}
